import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ArrowLeft, ArrowRight, Undo2, Eraser, Trash2, CheckCircle2,
  AlertCircle, Target, Loader2, Trophy, Hand, Sparkles,
} from 'lucide-react';
import { questionHelperAPI, handleAPIError } from '../services/api';
import { getDeviceId } from '../lib/deviceId';
import { tokenize, goldCategoryForToken, scoreAnnotations, judgeMark } from '../lib/qhScoring';
import { tokenStyleFor } from '../lib/categoryStyles';
import { AnnotatableText } from './qh/AnnotatableText';
import { getActiveSession, saveActiveSession, updateActiveSession } from '../lib/qhSession';

const SETUP_ROUTE = '/igcse-geography/question-helper/setup';
const DRAG_HINT_KEY = 'qh_seen_drag_hint_v1';

const QuestionHelper = () => {
  const navigate = useNavigate();
  const deviceId = useMemo(() => getDeviceId(), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legend, setLegend] = useState([]);
  const [items, setItems] = useState([]); // [{question, answer, tokens, goldByToken}]
  const [index, setIndex] = useState(0);

  // working state for the current question
  const [annotations, setAnnotations] = useState({});
  const [history, setHistory] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [studentMark, setStudentMark] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [summary, setSummary] = useState([]);
  const [showDragHint, setShowDragHint] = useState(() => {
    try { return localStorage.getItem(DRAG_HINT_KEY) !== '1'; } catch (_) { return true; }
  });

  const dismissDragHint = () => {
    setShowDragHint(false);
    try { localStorage.setItem(DRAG_HINT_KEY, '1'); } catch (_) { /* noop */ }
  };
  // Auto-dismiss the hint the moment a student performs their first real drag.
  const handleRangeApplied = (size) => { if (size > 1) dismissDragHint(); };

  const legendMap = useMemo(() => {
    const m = {};
    legend.forEach((c) => { m[c.category_code] = c; });
    return m;
  }, [legend]);
  const orderedCodes = useMemo(() => legend.map((c) => c.category_code), [legend]);

  useEffect(() => {
    (async () => {
      const session = getActiveSession();
      if (!session || !session.question_ids || !session.question_ids.length) {
        navigate(SETUP_ROUTE, { replace: true });
        return;
      }
      try {
        setLoading(true);
        await questionHelperAPI.seed();
        const [legendRes, practiceRes] = await Promise.all([
          questionHelperAPI.getLegend(),
          questionHelperAPI.getPractice(),
        ]);
        const cats = legendRes.categories || [];
        setLegend(cats);
        setActiveCategory(cats.length ? cats[0].category_code : null);

        const byId = {};
        (practiceRes.questions || []).forEach((q) => { byId[q.question_id] = q; });

        // Resolve the fixed session: each question_id in stored order, with its
        // pre-assigned hidden model answer. Render strictly from answer_text.
        const built = session.question_ids
          .map((qid) => {
            const q = byId[qid];
            if (!q) return null;
            const answerId = session.selected_answer_ids[qid];
            const answer = q.model_answers.find((m) => m.answer_id === answerId) || q.model_answers[0];
            const tokens = tokenize(answer.answer_text);
            const goldByToken = {};
            tokens.forEach((t) => {
              if (t.scored) goldByToken[t.index] = goldCategoryForToken(t, answer.segments);
            });
            return { question: q, answer, tokens, goldByToken };
          })
          .filter(Boolean);
        setItems(built);
        // Resume at the stored index (clamped).
        const resumeIdx = Math.min(session.current_question_index || 0, built.length);
        setIndex(resumeIdx);
        setStartedAt(Date.now());
      } catch (e) {
        setError(handleAPIError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const current = items[index];

  const pushHistory = () => setHistory((h) => [...h, annotations]);

  const applyRange = (from, to, category) => {
    if (submitted) return;
    pushHistory();
    setAnnotations((prev) => {
      const next = { ...prev };
      for (let i = from; i <= to; i += 1) {
        const tok = current.tokens.find((t) => t.index === i);
        if (!tok || !tok.scored) continue;
        if (category === 'ERASE') delete next[i];
        else next[i] = category;
      }
      return next;
    });
  };

  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      const last = h[h.length - 1];
      setAnnotations(last);
      return h.slice(0, -1);
    });
  };

  const clearAll = () => {
    if (submitted) return;
    pushHistory();
    setAnnotations({});
  };

  const handleSubmit = async () => {
    if (studentMark === null || submitted) return;
    const sc = scoreAnnotations(current.tokens, annotations, current.answer.segments);
    const mj = judgeMark(studentMark, current.answer.actual_mark);
    const res = { ...sc, ...mj, actualMark: current.answer.actual_mark, max: current.answer.maximum_mark };
    setResult(res);
    setSubmitted(true);
    if (mj.band === 'exact' && sc.accuracy >= 85) {
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
    }
    const record = {
      questionId: current.question.question_id,
      commandTerm: current.question.command_term,
      questionMarks: current.question.marks,
      ...res,
      studentMark,
    };
    setSummary((s) => [...s, record]);
    // Persist completion into the fixed session (anonymous, localStorage).
    const completed = Array.from(new Set([
      ...(getActiveSession()?.completed_question_ids || []),
      current.question.question_id,
    ]));
    updateActiveSession({ completed_question_ids: completed });
    try {
      await questionHelperAPI.saveAttempt({
        user_id: deviceId,
        question_id: current.question.question_id,
        answer_id: current.answer.answer_id,
        question_mode: 'independent_marker',
        student_mark: studentMark,
        actual_mark: current.answer.actual_mark,
        mark_difference: mj.difference,
        mark_judgement_band: mj.band,
        mark_judgement_score: mj.score,
        annotation_accuracy_percent: sc.accuracy,
        annotation_coverage_percent: sc.coverage,
        time_spent_seconds: Math.round((Date.now() - startedAt) / 1000),
      });
    } catch (e) {
      // progress save is best-effort for the pilot
      console.error('Attempt save failed', e);
    }
  };

  const goNext = () => {
    if (index + 1 >= items.length) {
      updateActiveSession({ current_question_index: items.length, status: 'completed' });
      setIndex(items.length); // show summary
      return;
    }
    const nextIdx = index + 1;
    updateActiveSession({ current_question_index: nextIdx });
    setIndex(nextIdx);
    setAnnotations({});
    setHistory([]);
    setStudentMark(null);
    setSubmitted(false);
    setResult(null);
    setStartedAt(Date.now());
  };

  const goToSetup = () => navigate(SETUP_ROUTE);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex items-center gap-3 text-slate-600" data-testid="qh-loading">
          <Loader2 className="w-6 h-6 animate-spin" /> Loading the Question Helper…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-6 text-center space-y-4">
            <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
            <p className="text-red-700" data-testid="qh-error">{error}</p>
            <Button onClick={() => navigate('/')} data-testid="qh-error-back-btn">Back to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ----------------------------- Summary screen -----------------------------
  if (index >= items.length) {
    const avgAcc = Math.round(summary.reduce((a, s) => a + s.accuracy, 0) / (summary.length || 1));
    const exacts = summary.filter((s) => s.band === 'exact').length;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center">
              <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-2" />
              <CardTitle className="text-3xl font-bold text-slate-800">Session complete</CardTitle>
              <p className="text-slate-600">You marked {summary.length} questions like an examiner.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center" data-testid="qh-summary-avg-accuracy">
                  <div className="text-3xl font-bold text-blue-700">{avgAcc}%</div>
                  <div className="text-sm text-slate-600">Average annotation accuracy</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center" data-testid="qh-summary-exact">
                  <div className="text-3xl font-bold text-emerald-700">{exacts}/{summary.length}</div>
                  <div className="text-sm text-slate-600">Exact mark judgements</div>
                </div>
              </div>
              {summary.map((s) => (
                <div key={s.questionId} className="flex items-center justify-between border rounded-lg p-3 gap-3 flex-wrap" data-testid={`qh-summary-row-${s.questionId}`}>
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-700">{s.questionId}</span>
                    <span className="text-xs text-slate-500" data-testid={`qh-summary-meta-${s.questionId}`}>
                      {s.commandTerm} · {s.questionMarks} mark{s.questionMarks === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span>Mark {s.studentMark}/{s.max} vs {s.actualMark}/{s.max}</span>
                    <Badge variant="secondary">{s.message}</Badge>
                    <span className="text-slate-600">Acc {s.accuracy}%</span>
                  </div>
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <Button onClick={goToSetup} className="flex-1" data-testid="qh-restart-btn">Build a new session</Button>
                <Button variant="outline" onClick={() => navigate('/')} className="flex-1" data-testid="qh-home-btn">Back to Home</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const q = current.question;
  const a = current.answer;
  const markOptions = Array.from({ length: q.marks + 1 }, (_, i) => i);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="qh-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Home
          </Button>
          <Badge variant="secondary" data-testid="qh-progress">Question {index + 1} of {items.length}</Badge>
        </div>

        {/* Question card */}
        <Card className="shadow-lg border-0">
          <CardHeader>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge data-testid="qh-paper">{q.paper_name}</Badge>
              <Badge variant="outline">{q.topic_name}</Badge>
              <Badge variant="outline">{q.command_term}</Badge>
              <Badge className="bg-slate-800" data-testid="qh-marks">{q.marks} mark{q.marks > 1 ? 's' : ''}</Badge>
            </div>
            <CardTitle className="text-xl text-slate-800" data-testid="qh-question-text">{q.question_text}</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Read the student's answer below, label each part, then judge the mark like an examiner.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Category palette */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Annotation tools — pick a tool, then tap or drag across words (keys 1–{legend.length})</p>
              <div className="flex flex-wrap gap-2">
                {legend.map((c, i) => (
                  <button
                    key={c.category_code}
                    type="button"
                    onClick={() => setActiveCategory(c.category_code)}
                    disabled={submitted}
                    data-testid={`qh-tool-${c.category_code}`}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all ${activeCategory === c.category_code ? 'ring-2 ring-offset-1 ring-blue-500 font-semibold' : 'opacity-90'} ${submitted ? 'cursor-not-allowed opacity-50' : 'hover:scale-105'}`}
                    style={{ backgroundColor: c.background_hex, color: c.text_hex, borderColor: c.border_hex }}
                    title={c.description}
                  >
                    <span className="font-bold text-xs">{i + 1}</span>
                    <span style={tokenStyleFor(c)}>{c.display_name}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setActiveCategory('ERASE')}
                  disabled={submitted}
                  data-testid="qh-tool-erase"
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm bg-white ${activeCategory === 'ERASE' ? 'ring-2 ring-offset-1 ring-blue-500 font-semibold' : ''} ${submitted ? 'cursor-not-allowed opacity-50' : 'hover:scale-105'}`}
                >
                  <Eraser className="w-4 h-4" /> Erase
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={undo} disabled={submitted || !history.length} data-testid="qh-undo-btn">
                  <Undo2 className="w-4 h-4 mr-1" /> Undo
                </Button>
                <Button size="sm" variant="outline" onClick={clearAll} disabled={submitted || !Object.keys(annotations).length} data-testid="qh-clear-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Clear all
                </Button>
              </div>
            </div>

            {/* One-time, fun drag-to-highlight coaching hint */}
            {showDragHint && !submitted && (
              <div className="qh-hint-in relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4" data-testid="qh-drag-hint">
                <button
                  type="button"
                  onClick={dismissDragHint}
                  data-testid="qh-drag-hint-dismiss"
                  className="absolute top-2 right-3 text-xs font-semibold text-blue-700 hover:text-blue-900"
                >
                  Got it ✓
                </button>
                <div className="flex items-center gap-4">
                  {/* Animated finger sweeping over a highlight trail */}
                  <div className="relative h-10 w-[160px] shrink-0" aria-hidden="true">
                    <div className="qh-drag-trail absolute top-3 left-0 h-5 rounded-md bg-yellow-300/70" />
                    <Hand className="qh-drag-hand absolute top-0 left-0 w-7 h-7 text-blue-700 drop-shadow" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 flex items-center gap-1">
                      <Sparkles className="w-4 h-4 text-amber-500" /> New: press and drag to highlight!
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Pick a tool above, then <strong>swipe across several words</strong> in one go — much faster than tapping each one. A single tap still works too.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Annotatable answer */}
            <div className="bg-white border rounded-xl p-4" data-testid="qh-answer-panel">
              <AnnotatableText
                tokens={current.tokens}
                annotations={annotations}
                legendMap={legendMap}
                orderedCodes={orderedCodes}
                activeCategory={activeCategory}
                onApplyRange={applyRange}
                onRangeApplied={handleRangeApplied}
                disabled={submitted}
                testIdPrefix="qh-token"
              />
            </div>

            {/* Mark selector */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">
                What whole-number mark would you award? <span className="text-slate-400 font-normal">(out of {q.marks})</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {markOptions.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => !submitted && setStudentMark(m)}
                    disabled={submitted}
                    data-testid={`qh-mark-${m}`}
                    className={`w-11 h-11 rounded-lg border text-lg font-semibold transition-all ${studentMark === m ? 'bg-blue-600 text-white border-blue-600 scale-105' : 'bg-white text-slate-700 hover:border-blue-400'} ${submitted ? 'cursor-not-allowed' : ''}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {!submitted && (
              <Button
                onClick={handleSubmit}
                disabled={studentMark === null}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white"
                data-testid="qh-submit-btn"
              >
                <Target className="w-4 h-4 mr-2" /> Submit and reveal the examiner's marking
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Reveal */}
        {submitted && result && (
          <Card className="shadow-xl border-0" data-testid="qh-reveal">
            <CardContent className="p-6 space-y-6">
              {/* Mark judgement */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-900 text-white rounded-xl p-5 text-center" data-testid="qh-actual-mark">
                  <div className="text-xs uppercase tracking-wide text-slate-300">Actual mark</div>
                  <div className="text-4xl font-bold">{result.actualMark}/{result.max}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-5 text-center" data-testid="qh-student-mark">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Your mark</div>
                  <div className="text-4xl font-bold text-blue-700">{studentMark}/{result.max}</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-5 text-center" data-testid="qh-difference">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Difference</div>
                  <div className="text-4xl font-bold text-amber-700">{result.difference}</div>
                  <div className="text-sm font-medium text-amber-800 mt-1">{result.message}</div>
                </div>
              </div>
              {a.level && (
                <div className="text-sm" data-testid="qh-level">
                  <span className="font-semibold text-slate-700">Correct level: </span>
                  <span className="text-slate-600">{a.level}</span>
                </div>
              )}

              {/* Annotation comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 border rounded-xl p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">My annotation</h4>
                  <AnnotatableText
                    tokens={current.tokens}
                    annotations={annotations}
                    legendMap={legendMap}
                    orderedCodes={orderedCodes}
                    activeCategory={null}
                    onApplyRange={() => {}}
                    disabled
                    testIdPrefix="qh-mine"
                  />
                </div>
                <div className="bg-emerald-50 border rounded-xl p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">Correct annotation</h4>
                  <AnnotatableText
                    tokens={current.tokens}
                    annotations={{}}
                    legendMap={legendMap}
                    orderedCodes={orderedCodes}
                    activeCategory={null}
                    onApplyRange={() => {}}
                    disabled
                    reveal
                    goldByToken={current.goldByToken}
                    testIdPrefix="qh-gold"
                  />
                </div>
              </div>

              {/* Accuracy + coverage */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center" data-testid="qh-accuracy">
                  <div className="text-3xl font-bold text-blue-700">{result.accuracy}%</div>
                  <div className="text-sm text-slate-600">Annotation accuracy</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center" data-testid="qh-coverage">
                  <div className="text-3xl font-bold text-purple-700">{result.coverage}%</div>
                  <div className="text-sm text-slate-600">Annotation coverage</div>
                </div>
              </div>

              {/* Feedback */}
              <div className="space-y-3">
                <div className="bg-white border rounded-xl p-4" data-testid="qh-mark-explanation">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Why this mark?</h4>
                  <p className="text-slate-600 text-sm">{a.mark_explanation}</p>
                </div>
                <div className="bg-white border rounded-xl p-4" data-testid="qh-improvement">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-1"><ArrowRight className="w-4 h-4 text-blue-600" /> Advice for reaching full marks</h4>
                  <p className="text-slate-600 text-sm">{a.improvement_to_full_marks}</p>
                </div>
              </div>

              <Button onClick={goNext} className="w-full" data-testid="qh-next-btn">
                {index + 1 >= items.length ? 'See summary' : 'Next question'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuestionHelper;
